// Centralised cart-change tracking with two distinct semantics:
//
// • GTM `cart_state` — a full cart snapshot on every basket change, INCLUDING
//   the persisted-state restore on page load, so abandoned-cart CDPs always
//   have the latest picture. Deduped by a value signature (the basket ref
//   settles in 2+ mutations on load: restore, then a server refresh) so one
//   real change fires once.
//
// • Dotdigital `cartUpdate` — fired ONLY on real cart mutations (add / remove /
//   update quantity) per Dotdigital's guidance, NOT on passive page-load
//   restore. So it hooks the cart store actions directly, not the watcher.
//   No-op unless the template enables Dotdigital + marketing consent (gating
//   lives in useDotdigital).
export default defineNuxtPlugin(() => {
	if (!import.meta.client) return;

	const cartStore = useCartStore();
	// The displayed basket, not `basket`: `updateBasket` (the discount code) and
	// `setBasket` store the basket unfiltered, and the watcher fires on exactly
	// those, so the payload would carry a bundle's child lines.
	const { displayBasket } = useDisplayBasket();
	const { cartState, cartCleared } = useGtmEvents();
	const { cartUpdate } = useDotdigital();

	const cartSignature = () => {
		const b = displayBasket.value;
		if (!b?.Items?.length) return "";
		return [
			b.Id,
			b.Summary?.Total?.AmountIncVat ?? "",
			b.DiscountCode ?? "",
			b.Items.map((i) => `${i.PartNo}x${i.Quantity}`).join(",")
		].join("|");
	};

	watch(
		cartSignature,
		() => {
			const next = displayBasket.value;
			if (!next || !next.Items?.length) {
				cartCleared();
				return;
			}
			cartState(next);
		},
		{ flush: "post" }
	);

	// The pack adds are their own actions, so a pack was not reaching
	// Dotdigital as a cart update. `addItemsToCart` stays out: it also runs as
	// the add-on tail of `addToCart` with `silent: true`, and one click must
	// not report twice.
	const CART_MUTATIONS = [
		"addToCart",
		"addManagedStructurePackToCart",
		"addMultiPackToCart",
		"removeFromCart",
		"updateCartItem"
	];
	cartStore.$onAction(({ name, after }) => {
		if (!CART_MUTATIONS.includes(name)) return;
		after(() => {
			const next = displayBasket.value;
			if (next?.Items?.length) cartUpdate(next);
		});
	});
});
